<template>
  <nav
    id="header"
    class="bg-white fixed w-full z-40 top-0 text-md border-b"
    data-testid="general-navigation"
  >
    <div class="container w-full mx-auto flex items-center">
      <div class="flex-1 pl-0">
        <div class="py-5">
          <router-link
            data-testid="general-navigation-tool-name"
            :to="RoutePath.ROOT"
          >
            <div class="flex">
              <img
                alt="Oliver Wyman"
                class="hide-until-lg h-6"
                src="/images/oliver-wyman.png"
              />
              <div v-if="clientLogo" class="flex">
                <div class="hide-until-lg w-5 h-10 border-r-2 border-grey" />
                <div class="hide-until-lg w-5 h-10"></div>
                <img
                  alt="Client logo"
                  class="hide-until-lg h-10"
                  :src="`/images/${clientLogo}`"
                />
              </div>
              <div class="hide-until-lg w-5 h-10 border-r-2 border-grey" />
              <div class="hide-until-lg w-5 h-10"></div>
              <img
                alt="Pharos"
                class="self-center inline-block h-8"
                src="/images/pharos.png"
              />
              <div v-if="isNotProduction" class="flex">
                <div class="hide-until-lg w-5 h-10 border-r-2 border-grey" />
                <div class="hide-until-lg h-10 pl-4">
                  <p class="pl-3 pr-3 py-2 text-white bg-red">
                    {{ $t(`general.non-production-environment`) }}
                  </p>
                </div>
              </div>
            </div>
          </router-link>
        </div>
      </div>

      <div
        class="flex-1 flex pr-0 mt-auto items-end justify-end"
        data-testid="general-navigation-links"
      >
        <template v-for="navItem in routes" :key="navItem.name">
          <div
            class="mr-4 my-0"
            :data-testid="`top-navigation-${navItem.name}`"
          >
            <div
              v-if="navItem.name === RouteName.LOGGED_OUT"
              class="nav-link"
              :class="isActiveClass(navItem)"
              data-testid="nav-link"
              :data-testkey="navItem.name"
              @click="requestLogout"
            >
              {{ $t(`navigation.${navItem.name}`) }}
            </div>
            <router-link
              v-else
              class="nav-link"
              :class="isActiveClass(navItem)"
              data-testid="nav-link"
              :data-testkey="navItem.name"
              :to="navItem.path"
            >
              {{ $t(`navigation.${navItem.name}`) }}
              <span
                v-if="notificationCounts[navItem.meta.notificationCountKey]"
                class="notification-count"
                :class="{
                  'notification-count-bg-red':
                    navItem.name === RouteName.LEAD_OVERVIEW,
                }"
              >
                {{ notificationCounts[navItem.meta.notificationCountKey] }}
              </span>
            </router-link>
          </div>
        </template>
      </div>
    </div>
  </nav>
</template>

<script lang="ts">
import { computed, defineComponent, inject, ref } from 'vue';
import { get, intersection } from 'lodash';
import { RoutePath, RouteName } from '@/enums/Routes';
import { Auth0ComputedState } from '@/services/integrations/auth0/types';
import router from '@/router';
import { authState } from '@/services/integrations/auth0';
import { Route } from '@/types/Global';
import { mainRoles, UserRole } from '@/enums/UserRole';
import { config } from '@/services/config';
import { getters as leadsGetters } from '@/store/modules/leads/getters';
import { getters as tasksGetters } from '@/store/modules/taskView/getters';
import { useStore } from '@/store';

export default defineComponent({
  name: 'TopNavigation',
  setup() {
    const auth = inject('auth') as Auth0ComputedState;
    const currentRoute = ref(router.currentRoute);
    const store = useStore();

    const requestLogout = () => {
      const currentRoute =
        window.location.protocol + '//' + window.location.host;
      auth.methods.logout({ returnTo: currentRoute + RoutePath.LOGGED_OUT });
    };

    const isActiveClass = (route: Route) => {
      const currentPath = get(currentRoute, 'value.path', '');
      const navItemRoutePath = get(route, 'path', false);
      return currentPath === navItemRoutePath ||
        currentPath.startsWith(navItemRoutePath + '/')
        ? 'router-link-active'
        : '';
    };

    const routes = (router.options.routes as Array<Route>)
      .filter(route => {
        const userRoles = get(authState, 'userRoles.value', []);
        const allowedRoles = get(
          route,
          'meta.allowedRoles',
          []
        ).filter((role: UserRole) => mainRoles.includes(role));

        return (
          route.meta &&
          route.meta.showInMainNav() &&
          route.meta.isEnabled() &&
          intersection(userRoles, allowedRoles).length > 0
        );
      })
      .sort((a, b) => get(a, 'meta.priority', 0) - get(b, 'meta.priority', 0));

    const clientLogo = computed(() => config.layout.options.clientLogo);

    const notificationCounts = computed(() => {
      return {
        leads: leadsGetters.getNewLeadsCount(store.state.leads),
        tasks: tasksGetters.getNewTasksCount(store.state.taskView),
        tasksAll: tasksGetters.getNewTasksAllCount(store.state.taskView),
      };
    });

    const isNotProduction =
      window.location.href.includes('release') ||
      window.location.href.includes('localhost') ||
      window.location.href.includes('dev');

    return {
      RouteName,
      RoutePath,
      isActiveClass,
      requestLogout,
      routes,
      clientLogo,
      notificationCounts,
      isNotProduction,
    };
  },
});
</script>

<style lang="postcss" scoped>
.nav-link {
  @apply pb-2 lg:pb-5 block py-3 pl-1 pr-1 align-middle text-gray-500 no-underline hover:text-gray-600 border-b-2 border-white hover:border-gray-600 cursor-pointer relative whitespace-nowrap;
}

.router-link-active {
  @apply text-primary no-underline hover:text-gray-600 border-primary hover:border-gray-600;
}

.hide-until-lg {
  @apply hidden lg:inline-block self-center;
}

.notification-count {
  @apply bg-primary text-white absolute right-1 -top-2 text-base leading-3 rounded-full flex items-center justify-center;
  padding: 2px;
  min-height: 1.25rem;
  min-width: 1.25rem;
}

.notification-count-bg-red {
  @apply bg-red;
}
</style>
